/**
 * «Задачи» — доска (новые, в работе, закрытые) и карточка задачи.
 *
 * Действия — те же функции, что у кнопок бота: «Взять в работу»,
 * «Закрыть» с объяснением, «Отменить» с причиной (только «Задачи ·
 * править»). Какие кнопки показать, решает сервер (`actions`).
 *
 * Задачу, требующую фото исправления, с 18.09.2026 закрывают и здесь
 * (D185, просьба владельца): снимок выбирается файлом или камерой
 * телефона, уезжает тем же путём, что снимок из бота, и только потом
 * задача закрывается. Порядок именно такой: закрытие без снимка сервер
 * не примет, а снимок без закрытия — просто снимок при задаче.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { Icon } from '../../icons';
import { Link, navigate } from '../../router';
import { Notice, Pill, isoDate } from '../../ui';
import { LIVE, shiftApi } from './api';
import type { TaskAnswer, TaskBoard, TaskCard as CardData, TaskRow } from './api';
import { tabPath } from './paths';
import { Photos, dayTime } from './shared';

export function Tasks({ taskId }: { taskId: number | null }) {
  return taskId !== null ? <TaskCard id={taskId} /> : <Board />;
}

const COLUMNS: { code: keyof TaskBoard['columns']; title: string; empty: string }[] = [
  { code: 'open', title: 'Новые', empty: 'Новых задач нет.' },
  { code: 'in_progress', title: 'В работе', empty: 'В работе ничего нет.' },
  { code: 'closed', title: 'Закрыто за неделю', empty: 'За неделю ничего не закрывали.' },
];

function Board() {
  const query = useQuery<TaskBoard, ApiError>({
    queryKey: ['shift', 'tasks'],
    queryFn: () => shiftApi.tasks(),
    ...LIVE,
  });
  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const board = query.data;
  const total = board.columns.open.length + board.columns.in_progress.length;

  return (
    <div className="shift-stack">
      <p className="shift-hint">
        {board.whole
          ? 'Все задачи ресторана. Задачи заводятся из чек-листов и кнопкой «Сообщить о проблеме» в боте.'
          : 'Задачи вашей должности и взятые вами. Заводятся из чек-листов и кнопкой «Сообщить о проблеме» в боте.'}
      </p>
      {total === 0 && board.columns.closed.length === 0 ? (
        <Empty title="Задач нет" text="Открытых задач для вас нет и за неделю ничего не закрывали." />
      ) : (
        <div className="shift-board">
          {COLUMNS.map((column) => {
            const rows = board.columns[column.code];
            return (
              <section className="shift-column" key={column.code} aria-label={column.title}>
                <h2 className="shift-column__title">
                  {column.title} <span className="faint">{rows.length}</span>
                </h2>
                {rows.length === 0 ? (
                  <p className="faint">{column.empty}</p>
                ) : (
                  rows.map((task) => <TaskTile key={task.id} task={task} />)
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TaskTile({ task }: { task: TaskRow }) {
  return (
    <Link to={tabPath('tasks', task.id)} className={`shift-task${task.overdue ? ' shift-task--late' : ''}`}>
      <span className="shift-task__title">{task.title}</span>
      <span className="faint">
        {[task.area, task.assignee ? `взял ${task.assignee}` : task.position]
          .filter(Boolean)
          .join(' · ')}
      </span>
      <span className="shift-task__meta">
        {task.active && task.due_at && (
          <span className={task.overdue ? 'shift-late' : 'faint'}>
            до {dayTime(task.due_at)}
            {task.overdue ? ', просрочена' : ''}
          </span>
        )}
        {!task.active && <Pill colour={task.status === 'resolved' ? 'green' : null}>{task.status_title}</Pill>}
        {task.photo_required && task.active && <span className="faint">нужно фото</span>}
      </span>
    </Link>
  );
}

type Mode = null | 'resolve' | 'cancel';

function TaskCard({ id }: { id: number }) {
  const client = useQueryClient();
  const [mode, setMode] = useState<Mode>(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const query = useQuery<CardData, ApiError>({
    queryKey: ['shift', 'task', id],
    queryFn: () => shiftApi.task(id),
    ...LIVE,
  });

  const done = {
    onSuccess: (answer: TaskAnswer) => {
      client.setQueryData(['shift', 'task', id], answer.card);
      setResult({ ok: true, text: answer.message });
      setMode(null);
      setText('');
      setFile(null);
    },
    onError: (error: ApiError) =>
      setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' }),
    onSettled: () => client.invalidateQueries({ queryKey: ['shift', 'tasks'] }),
  };
  const take = useMutation<TaskAnswer, ApiError, void>({ mutationFn: () => shiftApi.take(id), ...done });
  const resolve = useMutation<TaskAnswer, ApiError, void>({
    mutationFn: async () => {
      // Снимок первым: закрытие без него сервер не примет, а приложенный
      // к незакрытой задаче снимок никому не вредит.
      const photoId = file ? (await shiftApi.taskPhoto(id, file)).photo.photo_id : null;
      return shiftApi.resolve(id, text, photoId);
    },
    ...done,
  });
  const cancel = useMutation<TaskAnswer, ApiError, void>({
    mutationFn: () => shiftApi.cancel(id, text),
    ...done,
  });

  const back = (
    <button
      type="button"
      className="shift-back"
      onClick={() => (window.history.length > 1 ? window.history.back() : navigate('/shift/tasks'))}
    >
      <Icon name="back" size={16} /> К задачам
    </button>
  );
  if (query.isPending) return <Loading />;
  if (query.error) {
    return (
      <div className="shift-stack">
        {back}
        {query.error.status === 404 ? (
          <ErrorState title="Задачи нет" text="Её могли удалить. Вернитесь к доске." />
        ) : (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        )}
      </div>
    );
  }

  const card = query.data;
  const { task } = card;
  const busy = take.isPending || resolve.isPending || cancel.isPending;
  const open = (next: Mode) => {
    setResult(null);
    setText('');
    setFile(null);
    setMode(next);
  };

  return (
    <div className="shift-stack">
      {back}
      <article className="shift-card" aria-label="Задача">
        <header className="shift-card__head">
          <div>
            <h2 className="shift-card__title">{task.title}</h2>
            <p className="faint">
              {[task.area, task.position ? `для должности «${task.position}»` : 'адресована управляющему']
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <Pill colour={task.overdue ? 'red' : task.status === 'resolved' ? 'green' : 'yellow'}>
            {task.overdue ? 'просрочена' : task.status_title}
          </Pill>
        </header>

        {task.description && <p className="shift-task__description">{task.description}</p>}

        <dl className="shift-facts">
          {task.due_at && (
            <div>
              <dt>Срок</dt>
              <dd className={task.overdue ? 'shift-late' : undefined}>до {dayTime(task.due_at)}</dd>
            </div>
          )}
          <div>
            <dt>В работе у</dt>
            <dd>{task.assignee ?? 'ещё никто не взял'}</dd>
          </div>
          {card.source && (
            <div>
              <dt>Откуда</dt>
              <dd>
                {card.source.may_open ? (
                  <Link to={tabPath('checklists', card.source.run_id)}>
                    «{card.source.checklist}» {isoDate(card.source.date)}
                  </Link>
                ) : (
                  <>
                    «{card.source.checklist}» {isoDate(card.source.date)}
                  </>
                )}
                {card.source.item ? `, пункт «${card.source.item}»` : ''}
              </dd>
            </div>
          )}
          {task.comment && (
            <div>
              <dt>{task.status === 'cancelled' ? 'Почему отменена' : 'Что сделано'}</dt>
              <dd>{task.comment}</dd>
            </div>
          )}
        </dl>

        {card.photos.problem.length > 0 && (
          <div>
            <h3 className="shift-subtitle">Фото из чек-листа</h3>
            <Photos photos={card.photos.problem} alt={task.title} />
          </div>
        )}
        {card.photos.resolution.length > 0 && (
          <div>
            <h3 className="shift-subtitle">Фото исправления</h3>
            <Photos photos={card.photos.resolution} alt={`${task.title} — исправление`} />
          </div>
        )}

        <div>
          <h3 className="shift-subtitle">История</h3>
          <ul className="shift-history">
            {card.history.map((entry, index) => (
              <li key={index}>
                {entry.at && <span className="faint">{dayTime(entry.at)} · </span>}
                {entry.text}
              </li>
            ))}
          </ul>
        </div>

        {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}

        {card.actions.length > 0 && mode === null && (
          <div className="shift-buttons">
            {card.actions.includes('take') && (
              <button
                type="button"
                className="button button--primary"
                disabled={busy}
                onClick={() => {
                  setResult(null);
                  take.mutate();
                }}
              >
                Взять в работу
              </button>
            )}
            {card.actions.includes('resolve') && (
              <button type="button" className="button" disabled={busy} onClick={() => open('resolve')}>
                Закрыть
              </button>
            )}
            {card.actions.includes('cancel') && (
              <button type="button" className="button button--quiet" disabled={busy} onClick={() => open('cancel')}>
                Отменить задачу
              </button>
            )}
          </div>
        )}

        {(mode === 'resolve' || mode === 'cancel') && (
          <form
            className="shift-confirm"
            onSubmit={(event) => {
              event.preventDefault();
              setResult(null);
              if (mode === 'resolve') resolve.mutate();
              else cancel.mutate();
            }}
          >
            <label className="shift-field">
              <span>{mode === 'resolve' ? 'Что именно сделано' : 'Почему задача отменяется'}</span>
              <textarea
                className="input shift-textarea"
                rows={3}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </label>
            {mode === 'resolve' && (
              <div className="shift-field">
                <label htmlFor={`task-photo-${id}`}>
                  {task.photo_required ? 'Фото исправления' : 'Фото исправления, если нужно'}
                </label>
                <p className="faint">
                  {task.photo_required
                    ? 'Этот пункт закрывается со снимком. Снимите камерой или выберите файл.'
                    : 'Необязательно. Снимок останется в карточке задачи.'}
                </p>
                <input
                  id={`task-photo-${id}`}
                  ref={fileInput}
                  className="shift-file"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
                {file && (
                  <span className="faint">
                    {file.name}
                    {' · '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setFile(null);
                        if (fileInput.current) fileInput.current.value = '';
                      }}
                    >
                      убрать
                    </button>
                  </span>
                )}
              </div>
            )}
            <div className="shift-buttons">
              <button type="button" className="button" onClick={() => setMode(null)}>
                Назад
              </button>
              <button
                type="submit"
                className="button button--primary"
                disabled={
                  busy || !text.trim() || (mode === 'resolve' && task.photo_required && !file)
                }
              >
                {mode === 'resolve' ? 'Закрыть задачу' : 'Отменить задачу'}
              </button>
            </div>
          </form>
        )}
      </article>
    </div>
  );
}
